import { PlusOutlined, SearchOutlined, FileExcelOutlined, CopyOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { font } from '../../Common/font';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as PlanPrintIcon } from '../../Images/SimplePrintIcon.svg';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import { ReactComponent as ViewIcon } from '../../Images/ViewIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import {
  deleteWorkOrder,
  fetchWorkOrderStatistics,
  fetchWorkOrderList,
  resetWorkOrderData,
} from '../../slices/work-order';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import eventBus from '../../utils/eventBus';

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};
function WorkOrderList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(localStorage.getItem('WorkOrderList') || '');
  const [inputValue, setInputValue] = useState(localStorage.getItem('WorkOrderList') || '');
  const [pagesize, setPageSize] = useState(100);
  const csvLinkRef = useRef(null);
  const [csvData, setCsvData] = useState([]);
  const { data = [], statisticsData, loading, totalRecord } = useSelector(state => state.workOrder);
  const { user: currentUser } = useSelector(state => state.auth);
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const { data: settingData } = useSelector(state => state.setting);

  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const fetchWorkOrders = useCallback(
    async ({ page, pagesize, search, orderBy }) => {
      try {
        await dispatch(fetchWorkOrderList({ page, pagesize, ...(search && { search }), ...(orderBy && { ordering: orderBy }) })).unwrap();
      } catch (error) {
        message.error('Failed to fetch work orders.');
      } finally {
      }
    },
    [dispatch]
  );

  const fetchWorkOrderStatisticsData = useCallback(async () => {
    try {
      await dispatch(fetchWorkOrderStatistics());
    } catch (error) {
      message.error('Failed to fetch work orders.');
    } finally {
    }
  },
    [dispatch]
  );

  useEffect(() => {
    const handleReload = () => {
      fetchWorkOrders({ page: page, pagesize: pagesize, ...(search && { search }) });
      fetchWorkOrderStatisticsData();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchWorkOrders, fetchWorkOrderStatisticsData]);

  const debouncedFetch = useRef(
    debounce((searchTerm, page, pagesize, orderBy) => {

      fetchWorkOrders({ page, pagesize, search: searchTerm, orderBy });

    }, 500)
  ).current;

  useEffect(() => {
    fetchWorkOrders({ page, pagesize, search, orderBy });
  }, [page, pagesize, orderBy, fetchWorkOrders]);

  useEffect(() => {
    fetchWorkOrderStatisticsData();
  }, [fetchWorkOrderStatisticsData]);

  useEffect(() => {
    if (search !== undefined && search !== '' && search === inputValue && search.length >= 3) {
      debouncedFetch(search, 1, pagesize, orderBy);
      setPage(1);
    }
  }, [inputValue, search, debouncedFetch, orderBy, pagesize]);

  const handleSearch = e => {
    const value = e.target.value;
    if (value === '') {
      setSearch('');
      setInputValue('');
    } else {
      setInputValue(value);
      setSearch(value);
    }
    localStorage.setItem('WorkOrderList', value);
  };

  const handlePrintDownloadAll = async () => {
    const count = totalRecord;
    if (count > 0) {
      try {

        const response = await dispatch(
          fetchWorkOrderList({
            page: 1,
            pagesize: count,
            ...(search && { search }),
            ...(orderBy && { ordering: orderBy }),
          }),
        ).unwrap();
        const workorderDetails = response?.results?.data;
        if (workorderDetails && workorderDetails.length > 0) {
          const createPDF = (includePrintText, pdfData) => {
            const pdf = new jsPDF('p', 'pt', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            pdf.addFileToVFS('WorkSans-normal.ttf', font);
            pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
            pdf.setFont('WorkSans');
            const pdfColumns = [
              {
                header: 'Sr No.',
                dataKey: 'SrNo',
                field: 'SrNo'
              },
              {
                header: 'Order Date',
                dataKey: 'order_date',
                field: 'order_date',
              },
              {
                header: 'Order No',
                dataKey: 'order_no',
                field: 'order_no'
              },
              {
                header: 'PO No.',
                dataKey: 'purchase_order_no',
                field: 'purchase_order_no',
              },
              {
                header: 'Customer Name',
                dataKey: 'customer_name',
                field: 'customer_name',
              },
              {
                header: 'Total Weight',
                dataKey: 'total_weight',
                field: 'total_weight',
              },
              {
                header: 'Packed Weight',
                dataKey: 'total_packed_weight',
                field: 'total_packed_weight',
              },
              {
                header: 'Dispatched Weight',
                dataKey: 'total_dispatched_weight',
                field: 'total_dispatched_weight',
              },
              {
                header: 'Pending Weight',
                dataKey: 'total_pending_weight',
                field: 'total_pending_weight',
              },
              {
                header: 'Days Left',
                dataKey: 'days_left',
                field: 'days_left',
              },
              {
                header: 'Delivery Date',
                dataKey: 'delivery_date',
                field: 'delivery_date',
              },
              {
                header: 'Work Order Type',
                dataKey: 'workorder_type',
                field: 'workorder_type',
              },
              {
                header: 'Status',
                dataKey: 'status',
                field: 'status'
              },
              {
                header: 'Created At',
                dataKey: 'created_at',
                field: 'created_at',
              },
              {
                header: 'Created By',
                dataKey: 'created_by',
                field: 'created_by',
              },
              {
                header: 'Updated At',
                dataKey: 'updated_at',
                field: 'updated_at',
              },
              {
                header: 'Updated By',
                dataKey: 'updated_by',
                field: 'updated_by',
              },
            ];

            const pdffilteredColumns = pdfColumns.filter(column =>
              checkedList.includes(column.dataKey),
            );

            const getColorForDeliveryStatus = item => {
              const hasPlanningOrPacked =
                (item.status &&
                  ['closed'].includes(
                    String(item.status).trim().toLowerCase(),
                  )) ||
                item.work_order_details?.some(
                  el =>
                    el.status &&
                    ['closed'].includes(String(el.status).trim().toLowerCase()),
                );
              if (hasPlanningOrPacked) {
                return [153, 242, 163];
              }
              if (!item.order_date) return [255, 255, 255];
              const currentDate = dayjs();
              const orderDate = dayjs(item.order_date);
              const daysDiff = currentDate.diff(orderDate, 'day');
              if (daysDiff <= 0 || daysDiff <= 8) {
                return [255, 255, 255];
              } else if (daysDiff > 8 && daysDiff <= 12) {
                return [255, 255, 153];
              } else if (daysDiff > 12 && daysDiff <= 15) {
                return [255, 214, 153];
              } else {
                return [255, 179, 179];
              }
            };
            const getDaysLeftText = item => {
              const hasPlanningOrPacked =
                (item.status &&
                  ['closed'].includes(
                    String(item.status).trim().toLowerCase(),
                  )) ||
                item.work_order_details?.some(
                  el =>
                    el.status &&
                    ['closed'].includes(String(el.status).trim().toLowerCase()),
                );
              if (hasPlanningOrPacked) {
                return '-';
              }
              if (!item.order_date) return 'N/A';
              const currentDate = dayjs();
              const orderDate = dayjs(item.order_date);
              const daysDiff = currentDate.diff(orderDate, 'day');
              if (daysDiff === 0) {
                return 'Today';
              } else if (daysDiff < 0) {
                const absDiff = Math.abs(daysDiff);
                return absDiff === 1 ? '1 day left' : `${absDiff} days left`;
              } else {
                return daysDiff === 1 ? '1 day ago' : `${daysDiff} days ago`;
              }
            };
            const rows = pdfData.map((item, index) => {
              return {
                SrNo: index + 1,
                order_date: item?.order_date
                  ? dayjs(item.order_date).format('DD-MM-YYYY')
                  : 'N/A',
                order_no: item?.order_no || 'N/A',
                purchase_order_no: item?.purchase_order_no || 'N/A',
                customer_name: item?.customer?.customer_name || 'N/A',
                total_weight: item?.total_weight || 'N/A',
                total_packed_weight: item?.total_packed_weight ?? 'N/A',
                total_dispatched_weight: item?.total_dispatched_weight ?? '0',
                total_pending_weight: item?.total_pending_weight ?? 'N/A',
                days_left: getDaysLeftText(item),
                delivery_date: item?.delivery_date
                  ? dayjs(item.delivery_date).format('DD-MM-YYYY')
                  : 'N/A',
                workorder_type: item?.workorder_type || 'N/A',
                status: item?.status || 'N/A',
                created_at: item?.created_at
                  ? moment(item.created_at).format('DD-MM-YYYY')
                  : 'N/A',
                created_by: item?.created_by
                  ? `${item.created_by.first_name} ${item.created_by.last_name}`
                  : 'N/A',
                updated_at: item?.updated_at
                  ? moment(item.updated_at).format('DD-MM-YYYY')
                  : 'N/A',
                updated_by: item?.updated_by
                  ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
                  : 'N/A',

                _originalItem: item,
              };
            });
            pdf.text('WorkOrder', pdf.internal.pageSize.width / 2, 40, {
              align: 'center',
            });
            pdf.autoTable({
              head: [pdffilteredColumns.map(col => col.header)],
              body: rows.map(row =>
                pdffilteredColumns.map(col => row[col.dataKey]),
              ),
              startY: 65,
              theme: 'grid',
              styles: {
                font: 'times',
                halign: 'center',
                fontSize: 7,
                overflow: 'linebreak',
                cellPadding: 3.5,
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                textColor: [0, 0, 0],
              },
              headStyles: {
                textColor: [0, 0, 0],
                fontStyle: 'bold',
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fillColor: false,
              },
              tableLineColor: [0, 0, 0],
              rowPageBreak: 'avoid',
              margin: { top: 65, left: 20, right: 20, bottom: 30 },
              didDrawCell: data => {
                const { row, column, cell } = data;
                const rowData = rows[row.index];
                if (rowData && rowData._originalItem) {
                  const columnKey = pdffilteredColumns[column.index]?.dataKey;
                  if (
                    columnKey === 'delivery_date' ||
                    columnKey === 'days_left'
                  ) {
                    const color = getColorForDeliveryStatus(
                      rowData._originalItem,
                    );
                    if (
                      color[0] !== 255 ||
                      color[1] !== 255 ||
                      color[2] !== 255
                    ) {
                      pdf.setFillColor(color[0], color[1], color[2]);
                      pdf.rect(cell.x, cell.y, cell.width, cell.height, 'F');
                      pdf.setDrawColor(0, 0, 0);
                      pdf.setLineWidth(0.5);
                      pdf.rect(cell.x, cell.y, cell.width, cell.height, 'S');
                      pdf.setTextColor(0, 0, 0);
                      pdf.setFontSize(7);
                      pdf.text(
                        cell.text,
                        cell.x + cell.width / 2,
                        cell.y + cell.height / 2,
                        {
                          align: 'center',
                          baseline: 'middle',
                        },
                      );
                    }
                  }
                }
              },
              didDrawPage: data => {
                const pageNumber = pdf.internal.getNumberOfPages();
                const str = `Page ${pageNumber}`;
                const dateStr = new Date().toLocaleString();
                pdf.setFontSize(10);
                pdf.text(dateStr, 40, pageHeight - 20);
                pdf.text(str, pageWidth - 60, pageHeight - 20);
                if (pageNumber === 1) {
                  pdf.addImage(settingData?.company_settings?.company_logo, 'PNG', pageWidth - 100, 10, 90, 30);
                }
              },
            });

            return pdf;
          };
          const printPDF = createPDF(true, workorderDetails);
          const pdfBlob = printPDF.output('blob');
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed';
          iframe.style.right = '0px';
          iframe.style.bottom = '0px';
          iframe.style.width = '1px';
          iframe.style.height = '1px';
          iframe.style.border = 'none';
          iframe.src = URL.createObjectURL(pdfBlob);
          document.body.appendChild(iframe);
          iframe.onload = () => {

            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          };
          dispatch(
            createActivityLog({
              action: 'PRINT',
              module_name: 'WorkOrder',
              ip_address: ipAddress,
              discription: 'WorkOrder print download successfully!',
            }),
          );
          dispatch(resetWorkOrderData());
          await dispatch(
            fetchWorkOrderList({
              page,
              pagesize,
              ...(search && { search }),
              ...(orderBy && { ordering: orderBy }),
            })
          ).unwrap();
        } else {

          console.warn('No work order details found.');
        }
      } catch (error) {

        console.error('Failed to fetch or generate PDF:', error);
      }
    }
  };

  const handleEdit = useCallback(record => navigate(`/edit-work-order/${record.id}`), [navigate]);

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }
    dispatch(deleteWorkOrder(id))
      .unwrap()
      .then(() => {
        message.success('Work Order Archived Successfully!');
        fetchWorkOrders({ page, pagesize, search, orderBy });
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
          messages.length > 0
            ? message.error(messages.join('; '))
            : message.error('An unknown error occurred.');
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const [checkedList, setCheckedList] = useState([
    'SrNo', 'order_date', 'order_no', 'purchase_order_no', 'action', 'customer_name',
    'total_weight', 'total_packed_weight', 'total_pending_weight', 'total_dispatched_weight',
    'delivery_date', 'workorder_type', 'status', 'days_left',
  ]);

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
      render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      },
    },
    {
      title: 'Order Date',
      dataIndex: 'order_date',
      key: 'order_date',
      sorter: true,
      render: order_date => {
        return (
          <span>
            {order_date ? dayjs(order_date).format('DD-MM-YYYY') : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Order No.',
      dataIndex: 'order_no',
      key: 'order_no',
      render: order_no => {
        return <span>{order_no || 'N/A'}</span>;
      },
      sorter: true,
    },
    {
      title: 'PO No.',
      dataIndex: 'purchase_order_no',
      key: 'purchase_order_no',
      sorter: true,
      render: purchase_order_no => {
        return <span>{purchase_order_no || 'N/A'}</span>;
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      sorter: true,
      render: (_, record) => (
        <span>{record?.customer?.customer_name || 'N/A'}</span>
      ),
      key: 'customer_name',
    },
    {
      title: 'Total Weight',
      dataIndex: 'total_weight',
      key: 'total_weight',
      render: total_weight => {
        return <span>{total_weight ? total_weight : '0'}</span>;
      },
    },
    {
      title: 'Packed Weight',
      dataIndex: 'total_packed_weight',
      key: 'total_packed_weight',
      render: total_packed_weight => {
        return (
          <span>
            {total_packed_weight ? total_packed_weight : '0'}
          </span>
        );
      },
    },
    {
      title: 'Dispatched Weight',
      dataIndex: 'total_dispatched_weight',
      key: 'total_dispatched_weight',
      render: total_dispatched_weight => {
        return (
          <span>
            {total_dispatched_weight
              ? total_dispatched_weight
              : '0'}
          </span>
        );
      },
    },
    {
      title: 'Pending Weight',
      dataIndex: 'total_pending_weight',
      key: 'total_pending_weight',
      render: total_pending_weight => {
        return (
          <span>
            {total_pending_weight ? total_pending_weight : '0'}
          </span>
        );
      },
    },
    {
      title: 'Days Left',
      key: 'days_left',
      render: (text, record) => {
        const hasPlanningOrPacked =
          (record.status &&
            ['closed'].includes(String(record.status).trim().toLowerCase())) ||
          record.work_order_details?.some(
            el =>
              el.status &&
              ['closed'].includes(String(el.status).trim().toLowerCase()),
          );
        if (hasPlanningOrPacked) {
          return (
            <span
              style={{
                backgroundColor: '#99f2a3',
                color: 'black',
                textAlign: 'center',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
              }}
            >
              -
            </span>
          );
        }
        if (!record.order_date) return <span>N/A</span>;
        const currentDate = dayjs();
        const orderDate = dayjs(record.order_date);
        const daysDiff = currentDate.diff(orderDate, 'day');
        let textColor = 'black';
        let backgroundColor = undefined;
        if (daysDiff <= 0) {
          backgroundColor = undefined;
        } else if (daysDiff <= 8) {
          backgroundColor = undefined;
        } else if (daysDiff > 8 && daysDiff <= 12) {
          backgroundColor = '#ffff99';
        } else if (daysDiff > 12 && daysDiff <= 15) {
          backgroundColor = '#ffd699';
        } else {
          backgroundColor = '#ffb3b3';
        }
        let displayText;
        if (daysDiff === 0) {
          displayText = 'Today';
        } else if (daysDiff < 0) {
          const absDiff = Math.abs(daysDiff);
          displayText = absDiff === 1 ? '1 day left' : `${absDiff} days left`;
        } else {
          displayText = daysDiff === 1 ? '1 day ago' : `${daysDiff} days ago`;
        }
        return (
          <span
            style={{
              textAlign: 'center',
              backgroundColor: backgroundColor,
              color: textColor,
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              minWidth: '50px',
            }}
          >
            {displayText}
          </span>
        );
      },
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      sorter: true,
      render: (delivery_date, record) => {
        if (!delivery_date) return <span>N/A</span>;
        const hasPlanningOrPacked =
          (record.status &&
            ['closed'].includes(String(record.status).trim().toLowerCase())) ||
          record.work_order_details?.some(
            el =>
              el.status &&
              ['closed'].includes(String(el.status).trim().toLowerCase()),
          );
        if (hasPlanningOrPacked) {
          return (
            <span
              style={{
                backgroundColor: '#99f2a3',
                color: 'black',
                textAlign: 'center',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '90px',
              }}
            >
              {dayjs(delivery_date).format('DD-MM-YYYY')}
            </span>
          );
        }
        const currentDate = dayjs();
        const orderDate = dayjs(record.order_date);
        const daysDiff = currentDate.diff(orderDate, 'day');
        let textColor = 'black';
        let backgroundColor = undefined;
        if (daysDiff <= 0) {
          backgroundColor = undefined;
        } else if (daysDiff <= 8) {
          backgroundColor = undefined;
        } else if (daysDiff > 8 && daysDiff <= 12) {
          backgroundColor = '#ffff99';
        } else if (daysDiff > 12 && daysDiff <= 15) {
          backgroundColor = '#ffd699';
        } else {
          backgroundColor = '#ffb3b3';
        }
        return (
          <span
            style={{
              textAlign: 'center',
              backgroundColor: backgroundColor,
              color: textColor,
              padding: '2px 6px',
              borderRadius: '4px',
              display: 'inline-block',
              minWidth: '90px',
            }}
          >
            {dayjs(delivery_date).format('DD-MM-YYYY')}
          </span>
        );
      },
    },
    {
      title: 'Work Order Type',
      dataIndex: 'workorder_type',
      key: 'workorder_type',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: record => {
        return (
          <span>
            {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: record => (
        <Space>
          <>
            {currentUser.permissions.permissions.workorder.includes(
              'view_workorder',
            ) && (
                <Tooltip title="View">
                  <Link to={`/view-work-order-list/${record.id}`}>
                    <ViewIcon />
                  </Link>
                </Tooltip>
              )}
            {[
              'print_workorder_copy',
              'print_production_copy',
              'print_packing_copy',
              'print_account_sales_copy',
            ].some(permission =>
              currentUser.permissions.permissions.workorder.includes(
                permission,
              ),
            ) && (
                <a
                  href={`/print-work-order/${record.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Tooltip title="Print">
                    <PlanPrintIcon />
                  </Tooltip>
                </a>
              )}
          </>

          {currentUser.permissions.permissions.workorder.includes(
            'change_workorder',
          ) && (
              // record?.workorder_type !== 'Job Work' &&
              <Tooltip placement="top" title={'Edit'}>
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.workorder.includes(
            'delete_workorder',
          ) &&
            record?.workorder_type !== 'Job Work' && (
              <Tooltip title="Archive">
                <Popconfirm
                  title="Are you sure you want to archive this record?"
                  onConfirm={() => handleDelete(record.id)} // Correctly access `id`
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Link>
                    <Archive />
                  </Link>
                </Popconfirm>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={checkedValues => {
            handleColumnVisibilityChange(checkedValues);
          }}
        >
          <Row gutter={16}>
            <Col xs={12} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr.No.
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="order_date">Order Date</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="order_no">Order No</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="purchase_order_no">PO No.</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="customer_name">Customer Name</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="total_weight">Total Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="total_packed_weight">Packed Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="total_dispatched_weight">
                Dispatched Weight
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="total_pending_weight">Pending Weight</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="days_left">Days Left</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="delivery_date">Delivery Date</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="status">Status</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="workorder_type">Work Order Type</Checkbox>
            </Col>

            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const csvheaders = [
    { label: 'Sr. No', key: 'SrNo' },
    { label: 'Order Date', key: 'order_date' },
    { label: 'Order No.', key: 'order_no' },
    { label: 'PO No.', key: 'purchase_order_no' },
    { label: 'Customer Name', key: 'customer_name' },
    { label: 'Total Weight', key: 'total_weight' },
    { label: 'Packed Weight', key: 'total_packed_weight' },
    { label: 'Dispatched Weight', key: 'total_dispatched_weight' },
    { label: 'Pending Weight', key: 'total_pending_weight' },
    { label: 'Days Left', key: 'days_left' },
    { label: 'Delivery Date', key: 'delivery_date' },
    { label: 'Work Order Type', key: 'workorder_type' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const handleExcel = async () => {
    const count = totalRecord;
    if (count > 0) {
      try {
        const response = await dispatch(
          fetchWorkOrderList({
            page: 1,
            pagesize: count,
            ...(search && { search }),
            ...(orderBy && { ordering: orderBy }),
          })
        ).unwrap();

        const workOrderData = response.results?.data || response;

        // filter headers dynamically
        const selectedHeaders = csvFilterheaders.map(h => h.label);

        const excelData = workOrderData.map((item, index) => {
          const row = {
            'Sr. No': index + 1,
            'Order Date': item.order_date ? moment(item.order_date).format('DD-MM-YYYY') : 'N/A',
            'Order No.': item.order_no || 'N/A',
            'PO No.': item.purchase_order_no || 'N/A',
            'Customer Name': item?.customer?.customer_name || 'N/A',
            'Total Weight': item.total_weight || 0,
            'Packed Weight': item.total_packed_weight || 0,
            'Dispatched Weight': item.total_dispatched_weight || 0,
            'Pending Weight': item.total_pending_weight || 0,
            'Days Left': item.days_left ?? 'N/A',
            'Delivery Date': item.delivery_date ? moment(item.delivery_date).format('DD-MM-YYYY') : 'N/A',
            'Work Order Type': item.workorder_type || 'N/A',
            'Status': item.status || 'N/A',
            'Created At': item.created_at ? moment(item.created_at).format('DD-MM-YYYY') : 'N/A',
            'Created By': item.created_by ? `${item.created_by.first_name} ${item.created_by.last_name}` : 'N/A',
            'Updated At': item.updated_at ? moment(item.updated_at).format('DD-MM-YYYY') : 'N/A',
            'Updated By': item.updated_by ? `${item.updated_by.first_name} ${item.updated_by.last_name}` : 'N/A',
          };

          // keep only selected headers
          return Object.fromEntries(
            Object.entries(row).filter(([key]) => selectedHeaders.includes(key))
          );
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'WorkOrders');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, 'WorkOrder.xlsx');
      } catch (error) {
        console.error('Failed to fetch work order data:', error);
      } finally {

      }
    }
  };

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';
    if (sortField === 'SrNo' && sortOrder === 'ascend') {
      sortingEnumKey = 'SrNo';
    }
    if (sortField === 'SrNo' && sortOrder === 'descend') {
      sortingEnumKey = '-SrNo';
    }
    if (sortField === 'order_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'order_date';
    }
    if (sortField === 'order_date' && sortOrder === 'descend') {
      sortingEnumKey = '-order_date';
    }
    if (sortField === 'order_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'order_no';
    }
    if (sortField === 'order_no' && sortOrder === 'descend') {
      sortingEnumKey = '-order_no';
    }
    if (sortField === 'purchase_order_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'purchase_order_no';
    }
    if (sortField === 'purchase_order_no' && sortOrder === 'descend') {
      sortingEnumKey = '-purchase_order_no';
    }
    if (sortField === 'customer_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'customer_name__customer_name';
    }
    if (sortField === 'customer_name' && sortOrder === 'descend') {
      sortingEnumKey = '-customer_name__customer_name';
    }
    if (sortField === 'ship_to__customer_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'ship_to__customer_name';
    }
    if (sortField === 'ship_to__customer_name' && sortOrder === 'descend') {
      sortingEnumKey = '-ship_to__customer_name';
    }
    if (sortField === 'delivery_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'delivery_date';
    }
    if (sortField === 'delivery_date' && sortOrder === 'descend') {
      sortingEnumKey = '-delivery_date';
    }
    if (sortField === 'status' && sortOrder === 'ascend') {
      sortingEnumKey = 'status';
    }
    if (sortField === 'status' && sortOrder === 'descend') {
      sortingEnumKey = '-status';
    }
    if (sortField === 'workorder_type' && sortOrder === 'ascend') {
      sortingEnumKey = 'workorder_type';
    }
    if (sortField === 'workorder_type' && sortOrder === 'descend') {
      sortingEnumKey = '-workorder_type';
    }
    if (sortField === 'created_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__first_name';
    }
    if (sortField === 'created_by' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__first_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'created_by__last_name';
    }
    if (sortField === 'created_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-created_by__last_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__first_name';
    }
    if (sortField === 'updated_by' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__first_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'ascend') {
      sortingEnumKey = 'updated_by__last_name';
    }
    if (sortField === 'updated_by__last_name' && sortOrder === 'descend') {
      sortingEnumKey = '-updated_by__last_name';
    }
    if (sortField === 'total_weight' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_weight';
    }
    if (sortField === 'total_weight' && sortOrder === 'descend') {
      sortingEnumKey = '-total_weight';
    }
    if (sortField === 'total_packed_weight' && sortOrder === 'ascend') {
      sortingEnumKey = 'total_packed_weight';
    }
    if (sortField === 'total_packed_weight' && sortOrder === 'descend') {
      sortingEnumKey = '-total_packed_weight';
    }
    if (sortField === 'dispatched_weight' && sortOrder === 'ascend') {
      sortingEnumKey = 'dispatched_weight';
    }
    if (sortField === 'dispatched_weight' && sortOrder === 'descend') {
      sortingEnumKey = '-dispatched_weight';
    }
    return sortingEnumKey;
  };
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };
  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    localStorage.removeItem('WorkOrderList');
    setPage(1);
    fetchWorkOrders({
      page: 1,
      pagesize,
    });
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1,
  }));
  const dynamicColumns = columns.filter(column =>
    checkedList.includes(column.dataIndex || column.key),
  );
  const handleCopy = async () => {
    try {
      const workOrderDetails = data;

      if (workOrderDetails && workOrderDetails.length > 0) {
        const columnLabels = {
          SrNo: 'Sr. No.',
          order_date: 'Order Date',
          order_no: 'Order No.',
          purchase_order_no: 'PO No.',
          customer_name: 'Customer Name',
          total_weight: 'Total Weight',
          total_packed_weight: 'Packed Weight',
          total_dispatched_weight: 'Dispatched Weight',
          total_pending_weight: 'Pending Weight',
          delivery_date: 'Delivery Date',
          days_left: 'Days Left',
          workorder_type: 'Work Order Type',
          status: 'Status',
          created_at: 'Created At',
          created_by: 'Created By',
          updated_at: 'Updated At',
          updated_by: 'Updated By',
        };
        const filteredCheckedList = checkedList.filter(col => col !== 'action');
        const headers = filteredCheckedList.map(
          col => columnLabels[col] || col,
        );
        let tableText = headers.join('\t') + '\n';
        workOrderDetails.forEach((item, index) => {
          const row = filteredCheckedList.map(col => {
            // Sr. No. logic
            if (col === 'SrNo') {
              return (page - 1) * pagesize + index + 1;
            }

            // Days Left logic
            if (col === 'days_left') {
              const hasPlanningOrPacked =
                (item.status &&
                  ['closed'].includes(
                    String(item.status).trim().toLowerCase(),
                  )) ||
                item.work_order_details?.some(
                  el =>
                    el.status &&
                    ['closed'].includes(String(el.status).trim().toLowerCase()),
                );

              if (hasPlanningOrPacked) {
                return '-';
              }

              if (!item.order_date) return 'N/A';

              const currentDate = dayjs();
              const orderDate = dayjs(item.order_date);
              const daysDiff = currentDate.diff(orderDate, 'day');

              if (daysDiff === 0) {
                return 'Today';
              } else if (daysDiff < 0) {
                const absDiff = Math.abs(daysDiff);
                return absDiff === 1 ? '1 day left' : `${absDiff} days left`;
              } else {
                return daysDiff === 1 ? '1 day ago' : `${daysDiff} days ago`;
              }
            }

            if (col === 'customer_name') {
              console.log(item)
              return item?.customer?.customer_name || 'N/A';
            }

            if (col === 'total_packed_weight') {
              return `\u200B${Number(item[col]).toLocaleString('en-IN', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}`;
            }

            if (col === 'total_dispatched_weight') {
              return `\u200B${Number(item[col]).toLocaleString('en-IN', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}`;
            }

            if (col === 'total_pending_weight') {
              return `\u200B${Number(item[col]).toLocaleString('en-IN', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}`;
            }
            if (col === 'created_at' || col === 'updated_at') {
              return item[col]
                ? dayjs(item[col]).format('DD-MM-YYYY HH:mm')
                : 'N/A';
            }

            if (col === 'created_by') {
              if (!item[col]) return 'N/A';
              const first = item[col].first_name || '';
              const last = item[col].last_name || '';
              const fullName = `${first} ${last}`.trim();
              return fullName || 'N/A';
            }

            if (col === 'updated_by') {
              if (!item[col]) return 'N/A';
              const first = item[col].first_name || '';
              const last = item[col].last_name || '';
              const fullName = `${first} ${last}`.trim();
              return fullName || 'N/A';
            }
            // Default column value
            return item[col] ?? '';
          });

          tableText += row.join('\t') + '\n';
        });
        await navigator.clipboard.writeText(tableText);
      } else {
        console.warn('No workorder details found.');
      }
    } catch (error) {
      console.error('Failed to fetch or copy:', error);
    }
  };

  return (
    <div className="mainwrap">
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2>Work Orders</h2>
            <div className="bredcrumbwrp">
              <Link onClick={() => navigate(-1)} className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Work Orders' },
                ]}
              />
            </div>
          </Col>
          {/* Insert 3 color containers here */}

          <Col flex="0 0 auto">
            <div style={{ display: 'flex', gap: 16 }}>
              <div
                style={{
                  background: '#ffff99',
                  borderRadius: 8,
                  padding: '8px 20px',
                  minWidth: 90,
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16 }}>{statisticsData?.yellow}</div>
                <div style={{ fontSize: 12 }}>8-12 Days</div>
              </div>
              <div
                style={{
                  background: '#ffd699',
                  borderRadius: 8,
                  padding: '8px 20px',
                  minWidth: 90,
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16 }}>{statisticsData?.orange}</div>
                <div style={{ fontSize: 12 }}>13-15 Days</div>
              </div>
              <div
                style={{
                  background: '#ffb3b3',
                  borderRadius: 8,
                  padding: '8px 20px',
                  minWidth: 90,
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16 }}>{statisticsData?.red}</div>
                <div style={{ fontSize: 12 }}>{'> 15 Days'}</div>
              </div>
              <div
                style={{
                  background: '#99f2a3',
                  borderRadius: 8,
                  padding: '8px 20px',
                  minWidth: 90,
                  textAlign: 'center',
                  border: '1px solid #e0e0e0',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 16 }}>{statisticsData?.green}</div>
                <div style={{ fontSize: 12 }}>{'Closed'}</div>
              </div>
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={inputValue}
                style={{ width: 190 }}
                onChange={handleSearch}
                onPressEnter={() => { }} // Disable Enter key trigger if unwanted
              />
              <Link>
                <Tooltip title="Filter">
                  <Button
                    type="primary"
                    disabled={!search && !orderBy}
                    className="iconlink"
                    onClick={resetFilter}
                  >
                    <FilterIcon />
                  </Button>
                </Tooltip>
              </Link>
              {currentUser.permissions.permissions.workorder.includes(
                'download_workorder_excel_copy',
              ) && (
                  <Tooltip title={'Copy Work Order List to Clipboard'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handleCopy}
                    >
                      <CopyOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.workorder.includes(
                'download_workorder_excel_copy',
              ) && (
                  <Tooltip placement="top" title="Export Excel">
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={handleExcel} // Call handleExcel on button click

                    >
                      <FileExcelOutlined />
                      <CSVLink
                        filename="WorkOrder.csv"
                        className="iconlink"
                        style={{ display: 'none' }} // Hide the CSVLink
                        data={csvData}
                        headers={csvFilterheaders}
                        ref={csvLinkRef}
                      />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.workorder.includes(
                'print_workorder_copy',
              ) && (
                  <Tooltip title="Print">
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={handlePrintDownloadAll}
                    >
                      <PrintIcon />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.workorder.includes(
                'add_workorder',
              ) && (
                  <Link to="/create-work-order">
                    <Tooltip title="Create Work Order">
                      <Button type="primary" icon={<PlusOutlined />}>
                        Create Work Order
                      </Button>
                    </Tooltip>
                  </Link>
                )}
              <Link to="/work-order-filter-list">
                <Tooltip title="Open Work Order">
                  <Button type="primary">Open Work Order</Button>
                </Tooltip>
              </Link>
              {currentUser.permissions.permissions.workorder.includes(
                'delete_workorder',
              ) && (
                  <Link to="/archive-work-order-list" className="iconlink">
                    <Tooltip title="Archive">
                      <Archiveicon />
                    </Tooltip>
                  </Link>
                )}
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    open={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>
      <div className="tablewrp">
        <Table
          rowKey="id"
          // rowSelection={rowSelection}
          columns={dynamicColumns}
          dataSource={dataSource}
          pagination={false}
          loading={loading}
          scroll={{ x: '600px' }}
          onChange={handleTableChange}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />
    </div>
  );
}

export default WorkOrderList;
